import { useState, type ReactNode, type RefObject } from 'react'
import { Search } from 'lucide-react'
import { AnchoredPopover } from '@/components/ui/AnchoredPopover'
import { Avatar } from './Avatar'

export interface PersonOption {
  id: number
  name: string
  /** Linha de baixo (cargo). */
  subtitle?: string | null
  picture?: string | null
}

/**
 * Painel flutuante de escolher pessoa: busca pelo nome e lista com avatar.
 * Base de "Atribuir a…" (ocorrência e chamado do HUB) e de "Marcar colega".
 * Quem chama decide o que a escolha faz e se o painel fecha.
 */
export function PersonPicker({
  people,
  currentId = null,
  loading = false,
  error = false,
  emptyText = 'Ninguém encontrado',
  topRow,
  header,
  searchPlaceholder = 'Buscar pessoa…',
  onPick,
  onClose,
  anchorRef,
  alignRight = false,
}: {
  people: PersonOption[]
  /** Escolha atual (fica destacada). */
  currentId?: number | null
  loading?: boolean
  error?: boolean
  /** Texto quando não sobra ninguém (antes da busca). */
  emptyText?: string
  /** Linha extra antes das pessoas (ex.: "Sem responsável"). */
  topRow?: ReactNode
  /** Bloco acima da busca (ex.: quem já está marcado, com "remover"). */
  header?: ReactNode
  searchPlaceholder?: string
  onPick: (id: number) => void
  onClose: () => void
  anchorRef: RefObject<HTMLElement | null>
  alignRight?: boolean
}) {
  const [search, setSearch] = useState('')
  const q = search.trim().toLowerCase()
  const shown = q ? people.filter((p) => p.name.toLowerCase().includes(q)) : people

  return (
    <AnchoredPopover anchorRef={anchorRef} onClose={onClose} alignRight={alignRight}>
      {header}
      <div className="border-b border-border-subtle p-1.5">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-subtle" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-md border border-border-subtle bg-surface-muted py-1 pl-7 pr-2 text-[13px] text-body placeholder:text-subtle focus:border-blue-400 focus:outline-none"
          />
        </div>
      </div>
      <ul className="max-h-64 overflow-y-auto p-1">
        {topRow && <li>{topRow}</li>}
        {loading && <li className="px-2.5 py-2 text-[13px] text-subtle">Carregando…</li>}
        {error && (
          <li className="px-2.5 py-2 text-[13px] text-red-700 dark:text-red-400">
            Não foi possível carregar as pessoas.
          </li>
        )}
        {!loading && !error && shown.length === 0 && (
          <li className="px-2.5 py-2 text-[13px] text-subtle">
            {people.length === 0 ? emptyText : 'Ninguém encontrado'}
          </li>
        )}
        {shown.map((p) => (
          <li key={p.id}>
            <PickerRow onClick={() => onPick(p.id)} active={p.id === currentId}>
              <Avatar name={p.name} picture={p.picture ?? null} size={24} variant="soft" />
              <span className="min-w-0">
                <span className="block truncate text-body">{p.name}</span>
                {p.subtitle && (
                  <span className="block truncate text-[11px] text-muted">{p.subtitle}</span>
                )}
              </span>
            </PickerRow>
          </li>
        ))}
      </ul>
    </AnchoredPopover>
  )
}

/** Linha clicável do painel (pessoa ou opção extra). */
export function PickerRow({
  onClick,
  active = false,
  children,
}: {
  onClick: () => void
  active?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] transition ${
        active ? 'bg-blue-50 dark:bg-blue-900/25' : 'hover:bg-surface-muted'
      }`}
    >
      {children}
    </button>
  )
}
